//////////////////////////////////////////////////
// reducer with complex state, state that has interdepencies, business logic
//https://dev.to/spukas/3-reasons-to-usereducer-over-usestate-43ad
//https://www.robinwieruch.de/react-usereducer-vs-usestate
//
//https://medium.com/free-code-camp/why-you-should-choose-usestate-instead-of-usereducer-ffc80057f815


import { GridApi } from "ag-grid-community";
import { addDays } from "date-fns";
import _ from "lodash";
import { useCallback, useReducer } from "react";
import { IEntityValueTagModel } from "../../common/models/IEntityValueModels";
import { ITagCategory } from "../../common/models/ITagCategory";
import { ITagViewState } from "../../services/state-data-service/Models";
import storageService from "../../services/storage/storage-service/StorageService";
import { ITagHistory } from "../../services/tag-data-service/Models";
import { ITrendingDisplayOptions } from "../../services/trending-data-service/Models";
import { GridData } from "../ag-grid-extensions/models/GridData";
import { CustomDurationDialogValues } from "../custom-duration-dialog/CustomDurationDialog";
import CustomDurationUtils, { DurationOptionEnum } from "../custom-duration-dialog/CustomDurationUtils";
import ObjectGraphUtils from "../object-graph/ObjectGraphUtils";
import { useCustomerAttribute } from "../../queries/attributes/UseCustomerAttribute";
import { useRouteContext } from "../../hooks/use-route-context/UseRouteContext";

const defaultDetailsGraphDuration = 7;
const defaultDetailsGraphDurationAttributeName = "DefaultDetailsGraphDuration";

export enum ObjectTagValuesViewEnum {
    Chart = 0,
    Grid = 1
}

export type ObjectTagValuesChartData = {
    data: ITagHistory,
    tagDetailInfo: ITagViewState
}

export const OBJECT_VALUES_VIEW_OPTION_STORAGE_KEY = "ObjectValuesViewOptionsState";
export const OBJECT_VALUES_COLUMN_OPTIONS_STORAGE_KEY = "ObjectValuesColumnOptionsState";

function getInitialDate() {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    return date;
}

export type ObjectTagValuesColumnOptionsState = {
    showUIDColumn: boolean;
    showReadItemColumn: boolean;
    showWriteItemColumn: boolean;
    showLiveDataColumn: boolean;
    showLookupListColumn: boolean;
    showFormulaColumn: boolean;
}

type ObjectTagValuesViewOptionsState = {
    view: ObjectTagValuesViewEnum;
    durationLabel: string;
    durationOptions: CustomDurationDialogValues;
    showAlarmSetpointThreshold: boolean;
    showTagDescriptions: boolean;
    // TODO:
    //preferredCategories: number[];
    graphDisplayOptions: ITrendingDisplayOptions,
    selectedDate: Date,
    endDate: Date,
    configurationID: number | undefined;
    defaultConfigurationID: number | undefined;
    columnOptions: ObjectTagValuesColumnOptionsState;

    minY: number | undefined;
    maxY: number | undefined;
    forceMinMax: boolean;
    minMaxError: boolean;

}

export type ObjectTagValueState = {
    inWriteDownMode: boolean;
    gridApi: GridApi | undefined;
    data: GridData | undefined;
    commandInProgress: boolean;
    lastDemandScanTimestamp: Date | undefined;
    preWriteDownDemandScan: boolean | undefined;
    writeDownTags: IEntityValueTagModel[];
    categories: ITagCategory[];
    defaultCategory: ITagCategory | undefined;
    options: ObjectTagValuesViewOptionsState;
    isOptionsDirty: boolean;
    isRefreshing: boolean;
    downloadUid: string | undefined;
    dataError?: boolean;
};

function getDurationOption(defaultGraphDuration: number): DurationOptionEnum {
    switch (defaultGraphDuration) {
        case 1:
            return DurationOptionEnum.OneDay;
        case 3:
            return DurationOptionEnum.ThreeDays;
        case 7:
            return DurationOptionEnum.SevenDays;
        case 30:
            return DurationOptionEnum.ThirtyDays;
        default:
            return DurationOptionEnum.Custom;
    }
}

function getInitialColumnOptionsState(): ObjectTagValuesColumnOptionsState {
    return {
        showUIDColumn: false,
        showReadItemColumn: false,
        showWriteItemColumn: false,
        showLiveDataColumn: false,
        showLookupListColumn: false,
        showFormulaColumn: false    
    };
}

function getInitialOptionsState(defaultGraphDuration: number): ObjectTagValuesViewOptionsState {
    const durationOption = getDurationOption(defaultGraphDuration);

    return {
        view: ObjectTagValuesViewEnum.Grid,
        durationLabel: CustomDurationUtils.getCustomDurationLabel(durationOption, true),
        durationOptions: {
            option: durationOption,
            duration: {
                startDate: addDays(new Date(), -defaultGraphDuration),
                endDate: new Date()
            }
        },
        showAlarmSetpointThreshold: true,
        showTagDescriptions: false,
        graphDisplayOptions: ObjectGraphUtils.getDefaultDisplayOptions().displayOptions!,
        selectedDate: getInitialDate(),
        endDate: getInitialDate(),
        configurationID: -1,
        defaultConfigurationID: -1,
        columnOptions: getInitialColumnOptionsState(),

        minY: undefined,
        maxY: undefined,
        forceMinMax: false,
        minMaxError: false,

    };
}

// Getting the local storage settings in the const initialState did not work because
// they are only loaded the first time the application loads. A function fixed it.
function getInitialState(defaultGraphDuration: number) {

    const initialState: ObjectTagValueState = {
        inWriteDownMode: false,
        gridApi: undefined,
        data: undefined,
        commandInProgress: false,
        lastDemandScanTimestamp: undefined,
        preWriteDownDemandScan: undefined,
        writeDownTags: [],
        categories: [],
        defaultCategory: undefined,
        isOptionsDirty: false,
        options: getInitialOptionsState(defaultGraphDuration),
        isRefreshing: false,
        downloadUid: undefined,
        dataError: false
    };

    // if reading options state from local storage, dates will be strings, so they have to be 
    // fixed here
    const storedOptionState: ObjectTagValuesViewOptionsState | undefined = storageService.local.get<ObjectTagValuesViewOptionsState | undefined>(OBJECT_VALUES_VIEW_OPTION_STORAGE_KEY, undefined);

    if (storedOptionState) {
        //durationOptions: CustomDurationDialogValues;
        storedOptionState.selectedDate = new Date(storedOptionState.selectedDate.toString());
        storedOptionState.endDate = new Date(storedOptionState.endDate.toString());
        if (storedOptionState.durationOptions.duration.startDate) {
            storedOptionState.durationOptions.duration.startDate = new Date(storedOptionState.durationOptions.duration.startDate.toString());
        }
        if (storedOptionState.durationOptions.duration.endDate) {
            storedOptionState.durationOptions.duration.endDate = new Date(storedOptionState.durationOptions.duration.endDate.toString());
        }

        initialState.options = storedOptionState;
    }

    const storedColumnOptionsState: ObjectTagValuesColumnOptionsState | undefined = storageService.local.get<ObjectTagValuesColumnOptionsState | undefined>(OBJECT_VALUES_COLUMN_OPTIONS_STORAGE_KEY, undefined);

    if (storedColumnOptionsState) {
        initialState.options.columnOptions = storedColumnOptionsState;
    }

    return initialState;

    // const newState = {
    //     ...initialState,
    //     options: storedOptionState ?? getInitialOptionsState(defaultGraphDuration)
    // };

    // return newState;
}

enum ObjectTagValueActionType {
    InWriteDownModeChanged = 'IN_WRITEDOWN_MODE_CHANGED',
    GridApiChanged = 'GRID_API_CHANGED',
    DataChanged = 'DATA_CHANGED',
    CommandInProgressChanged = 'COMMAND_IN_PROGRESS_CHANGED',
    LastDemandScanTimestampChanged = 'LAST_DEMANDSCAN_TIMESTAMP_CHANGED',
    PreWriteDownBeforeDemandScanChanged = 'PRE_WRITEDOWN_BEFORE_DEMAND_SCAN_CHANGED',
    ResetLiveDataStateChanged = 'RESET_LIVEDATA_STATE_CHANGED',
    WriteDownTagsChanged = 'WRITE_DOWN_TAGS_CHANGED',
    CategoriesChanged = 'CATEGORIES_CHANGED',
    DefaultCategoryChanged = 'DEFAULT_CATEGORY_CHANGED',
    ResetOptionsRequested = 'RESET_OPTIONS_REQUESTED',
    ViewChanged = 'VIEW_CHANGED',
    DurationChanged = 'DURATION_CHANGED',
    AlarmSetpointThresholdChanged = "ALARM_SETPOINT_THRESHOLD_CHANGED",
    TagDescriptionsChanged = "TAG_DESCRIPTIONS_CHANGED",
    GraphDisplayOptionsChanged = "GRAPH_DISPLAY_OPTIONS_CHANGED",
    SelectedDateChanged = "SELECTED_DATE_CHANGED",
    EndDateChanged = "END_DATE_CHANGED",
    IsRefreshingChanged = "IS_REFRESHING_CHANGED",
    ResetDefaultDuration = 'RESET_DEFAULT_DURATION',
    DownloadUidChanged = 'DOWNLOAD_UID_CHANGED',
    DataErrorChanged = 'TAG_VALUES_DATA_ERROR_CHANGED',
    ConfigurationIDChanged = "CONFIGURATION_ID_CHANGED",
    DefaultConfigurationIDChanged = "DEFAULT_CONFIGURATION_ID_CHANGED",
    ColumnOptionsChanged = "COLUMN_OPTIONS_CHANGED",
    ForceMinMaxChanged = 'FORCE_MIN_MAX_Y_CHANGED',
    MinMaxYChanged = 'MIN_MAX_Y_CHANGED',
    MinMaxErrorChanged = 'MIN_MAX_ERROR_CHANGED',

}

type ObjectTagValueActions = | {
    type: ObjectTagValueActionType.InWriteDownModeChanged;
    payload: boolean;
} | {
    type: ObjectTagValueActionType.GridApiChanged;
    payload: GridApi | undefined;
} | {
    type: ObjectTagValueActionType.DataChanged;
    payload: GridData | undefined;
} | {
    type: ObjectTagValueActionType.CommandInProgressChanged;
    payload: boolean;
} | {
    type: ObjectTagValueActionType.LastDemandScanTimestampChanged;
    payload: Date;
} | {
    type: ObjectTagValueActionType.PreWriteDownBeforeDemandScanChanged;
    payload: boolean;
} | {
    type: ObjectTagValueActionType.ResetLiveDataStateChanged;
    payload: void;
} | {
    type: ObjectTagValueActionType.WriteDownTagsChanged;
    payload: IEntityValueTagModel[];
} | {
    type: ObjectTagValueActionType.CategoriesChanged;
    payload: ITagCategory[]
} | {
    type: ObjectTagValueActionType.DefaultCategoryChanged;
    payload: ITagCategory
} | {
    type: ObjectTagValueActionType.ResetOptionsRequested;
} | {
    type: ObjectTagValueActionType.ViewChanged;
    payload: ObjectTagValuesViewEnum
} | {
    type: ObjectTagValueActionType.DurationChanged;
    payload: CustomDurationDialogValues
} | {
    type: ObjectTagValueActionType.AlarmSetpointThresholdChanged;
    payload: boolean
} | {
    type: ObjectTagValueActionType.GraphDisplayOptionsChanged;
    payload: ITrendingDisplayOptions
} | {
    type: ObjectTagValueActionType.ResetDefaultDuration;
    payload: void;
} | {
    type: ObjectTagValueActionType.SelectedDateChanged;
    payload: Date;
} | {
    type: ObjectTagValueActionType.EndDateChanged
    payload: Date;
} | {
    type: ObjectTagValueActionType.IsRefreshingChanged
    payload: boolean;
} |  {
    type: ObjectTagValueActionType.DownloadUidChanged;
    payload: string | undefined;
} | {
    type: ObjectTagValueActionType.TagDescriptionsChanged;
    payload: boolean
} | {
    type: ObjectTagValueActionType.DataErrorChanged;
    payload: boolean
} | {    
    type: ObjectTagValueActionType.ConfigurationIDChanged;
    configurationIDOrCallback: number | undefined | ((configurationID: number | undefined) => number | undefined);
} | {
    type: ObjectTagValueActionType.DefaultConfigurationIDChanged;
    defaultConfigurationID: number | undefined;
} | {
    type: ObjectTagValueActionType.MinMaxYChanged;
    payload: [number | undefined, number | undefined];
} |  {
    type: ObjectTagValueActionType.ForceMinMaxChanged;
    payload: boolean;
} |  {
    type: ObjectTagValueActionType.MinMaxErrorChanged;
    payload: boolean;
} |  {
    type: ObjectTagValueActionType.ColumnOptionsChanged;
    optionsOrCallback: ObjectTagValuesColumnOptionsState | ((options: ObjectTagValuesColumnOptionsState) => ObjectTagValuesColumnOptionsState);
};

function isOptionsDirty(value: ObjectTagValueState, defaultGraphDuration: number) {
    const options = getInitialState(defaultGraphDuration).options;

    return value.defaultCategory?.id !== value.categories.find(o => o.selected)?.id ||
        value.options.view !== options.view ||
        !_.isEqual(value.options.graphDisplayOptions, options.graphDisplayOptions) || 
        value.options.durationOptions.option !== options.durationOptions.option ||
        value.options.durationOptions.duration.startDate?.valueOf() !== options.durationOptions.duration.startDate?.valueOf() ||
        value.options.durationOptions.duration.endDate?.valueOf() !== options.durationOptions.duration.endDate?.valueOf() ||
        value.options.showAlarmSetpointThreshold !== options.showAlarmSetpointThreshold ||
        value.options.showTagDescriptions !== options.showTagDescriptions ||
        value.options.selectedDate.valueOf() !== options.selectedDate.valueOf() ||
        value.options.endDate.valueOf() !== options.endDate.valueOf() ||
        value.options.minY !== options.minY ||
        value.options.maxY !== options.maxY;

}

export default function useObjectTagValueState() {

    const routeContext = useRouteContext();
    
    const {data} = useCustomerAttribute({
        groupID: routeContext.groupID!,
        name: defaultDetailsGraphDurationAttributeName,
        enabled: true
    });

    const defaultGraphDuration = Number.parseInt(data?.attributeValue ?? `${defaultDetailsGraphDuration}`);

    // TODO: Why isn't this using useReducer?
    const objectTagValuesReducer = useCallback((state: ObjectTagValueState, action: ObjectTagValueActions): ObjectTagValueState => {
        
        function saveOptionsToStorage(options: ObjectTagValuesViewOptionsState) {
            const { columnOptions, ...optionsWithoutColumnOptions } = options;
            storageService.local.set(OBJECT_VALUES_VIEW_OPTION_STORAGE_KEY, optionsWithoutColumnOptions);
        }

        function saveColumnOptionsToStorage(columnOptions: ObjectTagValuesColumnOptionsState) {
            storageService.local.set(OBJECT_VALUES_COLUMN_OPTIONS_STORAGE_KEY, columnOptions);
        }

        const newState:ObjectTagValueState = {...state,
    
            options: {
                ...state.options,
                    graphDisplayOptions: {...state.options.graphDisplayOptions},
                    durationLabel: state.options.durationLabel,
                    durationOptions: {
                        option: state.options.durationOptions.option,
                        duration: {
                            startDate: new Date(state.options.durationOptions.duration.startDate as Date),
                            endDate: new Date(state.options.durationOptions.duration.endDate as Date)
                        }
                    } as CustomDurationDialogValues
            }
        };
    
        switch (action.type) {
    
            case ObjectTagValueActionType.InWriteDownModeChanged:
                return {
                    ...state,
                    inWriteDownMode: action.payload
                };
    
            case ObjectTagValueActionType.GridApiChanged:
                return {
                    ...state,
                    gridApi: action.payload
                };
    
            case ObjectTagValueActionType.DataChanged:
                return {
                    ...state,
                    isOptionsDirty: false,
                    data: action.payload
                };
    
            case ObjectTagValueActionType.CommandInProgressChanged:
                return {
                    ...state,
                    commandInProgress: action.payload
                };
    
            case ObjectTagValueActionType.LastDemandScanTimestampChanged:
                return {
                    ...state,
                    lastDemandScanTimestamp: action.payload
                };
    
            case ObjectTagValueActionType.PreWriteDownBeforeDemandScanChanged:
                return {
                    ...state,
                    preWriteDownDemandScan: action.payload
                };
    
            case ObjectTagValueActionType.ResetLiveDataStateChanged:
                return {
                    ...state,
                    commandInProgress: false,
                    preWriteDownDemandScan: false,
                    inWriteDownMode: false
                }
    
            case ObjectTagValueActionType.WriteDownTagsChanged:
                return {
                    ...state,
                    writeDownTags: action.payload
                }
    
            case ObjectTagValueActionType.CategoriesChanged:
                newState.categories = action.payload;
                newState.isOptionsDirty = isOptionsDirty(newState, defaultGraphDuration);
                return newState;
    
            case ObjectTagValueActionType.DefaultCategoryChanged:
                newState.defaultCategory = action.payload;
                newState.isOptionsDirty = isOptionsDirty(newState, defaultGraphDuration);
                return newState;
    
            case ObjectTagValueActionType.GraphDisplayOptionsChanged:
                newState.options = {...newState.options,
                graphDisplayOptions: action.payload
                };
                newState.isOptionsDirty = isOptionsDirty(newState, defaultGraphDuration);
                return newState;
        
            case ObjectTagValueActionType.ResetOptionsRequested:
                storageService.local.remove(OBJECT_VALUES_VIEW_OPTION_STORAGE_KEY);
                newState.categories =  state.categories.map(o => {
                    o.selected = o.id === state.defaultCategory?.id;
                    return o;
                });
                
                newState.options = {
                    ...getInitialState(defaultGraphDuration).options,
                    graphDisplayOptions: { ...getInitialState(defaultGraphDuration).options.graphDisplayOptions},
                    durationOptions: {
                        option: getInitialState(defaultGraphDuration).options.durationOptions.option,
                        duration: {
                            startDate: new Date(getInitialState(defaultGraphDuration).options.durationOptions.duration.startDate as Date),
                            endDate: new Date(getInitialState(defaultGraphDuration).options.durationOptions.duration.endDate as Date)
                        }
                    }
                }
                // newState.view = initialState.view;
                // newState.durationOptions = {
                //     option: initialState.durationOptions.option,
                //     duration: {
                //         startDate: new Date(initialState.durationOptions.duration.startDate as Date),
                //         endDate: new Date(initialState.durationOptions.duration.endDate as Date)
                //     }
                // };
                // newState.durationLabel = initialState.durationLabel;
                // newState.showAlarmSetpointThreshold = initialState.showAlarmSetpointThreshold;
                // newState.showTagDescriptions = initialState.showTagDescriptions;
    
                newState.isOptionsDirty = isOptionsDirty(newState, defaultGraphDuration);
                return newState;
    
            case ObjectTagValueActionType.ViewChanged:
                newState.options.view = action.payload;
                newState.isOptionsDirty = isOptionsDirty(newState, defaultGraphDuration);
                //storageService.local.set(OBJECT_VALUES_VIEW_OPTION_STORAGE_KEY, newState.options);
                saveOptionsToStorage(newState.options);
                return newState;
    
            case ObjectTagValueActionType.DurationChanged:
    
                newState.options = {
                    ...state.options,
                    durationLabel: CustomDurationUtils.getCustomDurationLabel(action.payload.option, true),
                    durationOptions: {
                        option: action.payload.option,
                        duration: {
                            startDate: new Date(action.payload.duration.startDate as Date),
                            endDate: new Date(action.payload.duration.endDate as Date)
                        }
                    }
                }
    
                newState.isOptionsDirty = isOptionsDirty(newState, defaultGraphDuration);
                //storageService.local.set(OBJECT_VALUES_VIEW_OPTION_STORAGE_KEY, newState.options);
                saveOptionsToStorage(newState.options);
                return newState;
    
            case ObjectTagValueActionType.ResetDefaultDuration:
                newState.options.durationLabel = getInitialState(defaultGraphDuration).options.durationLabel;
                newState.options.durationOptions = {
                    option: getInitialState(defaultGraphDuration).options.durationOptions.option,
                    duration: {
                        startDate: new Date(getInitialState(defaultGraphDuration).options.durationOptions.duration.startDate as Date),
                        endDate: new Date(getInitialState(defaultGraphDuration).options.durationOptions.duration.endDate as Date)
                    }
                }
                newState.isOptionsDirty = isOptionsDirty(newState, defaultGraphDuration);
                return newState;
    
            case ObjectTagValueActionType.AlarmSetpointThresholdChanged:
                newState.options.showAlarmSetpointThreshold = action.payload;
                newState.isOptionsDirty = isOptionsDirty(newState, defaultGraphDuration);
                //storageService.local.set(OBJECT_VALUES_VIEW_OPTION_STORAGE_KEY, newState.options);
                saveOptionsToStorage(newState.options);
                return newState;
    
            case ObjectTagValueActionType.TagDescriptionsChanged:
                newState.options.showTagDescriptions = action.payload;
                newState.isOptionsDirty = isOptionsDirty(newState, defaultGraphDuration);
                //storageService.local.set(OBJECT_VALUES_VIEW_OPTION_STORAGE_KEY, newState.options);
                saveOptionsToStorage(newState.options);
                return newState;
    
            case ObjectTagValueActionType.SelectedDateChanged:
                newState.options.selectedDate = action.payload;
                newState.isOptionsDirty = isOptionsDirty(newState, defaultGraphDuration);
                return newState;
    
            case ObjectTagValueActionType.EndDateChanged:
                newState.options.endDate = action.payload;
                newState.isOptionsDirty = isOptionsDirty(newState, defaultGraphDuration);
                return newState;
    
            case ObjectTagValueActionType.IsRefreshingChanged:
                newState.isRefreshing = action.payload;
                return newState;
    
            case ObjectTagValueActionType.DownloadUidChanged:
                newState.downloadUid = action.payload
                return newState;            

            case ObjectTagValueActionType.DataErrorChanged:
                newState.dataError = action.payload;
                return newState;
        
            case ObjectTagValueActionType.ConfigurationIDChanged:
                newState.options.configurationID = typeof action.configurationIDOrCallback === "function"
                    ? action.configurationIDOrCallback(state.options.configurationID)
                    : action.configurationIDOrCallback;
                newState.isOptionsDirty = isOptionsDirty(newState, defaultGraphDuration);
                return newState;            

            case ObjectTagValueActionType.ColumnOptionsChanged:
                newState.options.columnOptions = typeof action.optionsOrCallback === "function"
                    ? action.optionsOrCallback(state.options.columnOptions)
                    : action.optionsOrCallback;
                newState.isOptionsDirty = isOptionsDirty(newState, defaultGraphDuration);
                //storageService.local.set(OBJECT_VALUES_COLUMN_OPTIONS_STORAGE_KEY, newState.options);
                saveColumnOptionsToStorage(newState.options.columnOptions);
                return newState;            

            case ObjectTagValueActionType.DefaultConfigurationIDChanged:
                newState.options.defaultConfigurationID = action.defaultConfigurationID;
                newState.isOptionsDirty = isOptionsDirty(newState, defaultGraphDuration);
                return newState;

            case ObjectTagValueActionType.ForceMinMaxChanged:
                newState.options.forceMinMax = action.payload;
                newState.options.minY = undefined;
                newState.options.maxY = undefined;
                return newState;

            case ObjectTagValueActionType.MinMaxErrorChanged:
                newState.options.minMaxError = action.payload;
                return newState;

            case ObjectTagValueActionType.MinMaxYChanged:
                newState.options.minY = action.payload.at(0);
                newState.options.maxY = action.payload.at(1);
                newState.isOptionsDirty = isOptionsDirty(newState, defaultGraphDuration);
                return newState;

            default:
                return state;
        }
    }, [defaultGraphDuration]);
    
    // by using a custom hook we could include  other state to work on such as app settings state
    // also this simplifies use of dispatch by providing state methods, this would alleviate the
    // need to use an enum for the action types
    const [state, dispatch] = useReducer(objectTagValuesReducer, getInitialState(defaultGraphDuration));

    const setInWriteDownMode = useCallback((value: boolean) => dispatch({
        type: ObjectTagValueActionType.InWriteDownModeChanged,
        payload: value
    }), []);

    const setGridApi = useCallback((value: GridApi | undefined) => dispatch({
        type: ObjectTagValueActionType.GridApiChanged,
        payload: value
    }), []);

    const setData = useCallback((value: GridData | undefined) => dispatch({
        type: ObjectTagValueActionType.DataChanged,
        payload: value
    }), []);

    const setCommandInProgress = useCallback((value: boolean) => dispatch({
        type: ObjectTagValueActionType.CommandInProgressChanged,
        payload: value
    }), []);

    const setLastDemandScanTimestamp = useCallback((value: Date) => dispatch({
        type: ObjectTagValueActionType.LastDemandScanTimestampChanged,
        payload: value
    }), []);

    const setPreWriteDownBeforeDemandScan = useCallback((value: boolean) => dispatch({
        type: ObjectTagValueActionType.PreWriteDownBeforeDemandScanChanged,
        payload: value
    }), []);

    const resetLiveDataState = useCallback(() => dispatch({
        type: ObjectTagValueActionType.ResetLiveDataStateChanged,
        payload: undefined
    }), []);

    const setWriteDownTags = useCallback((value: IEntityValueTagModel[]) => dispatch({
        type: ObjectTagValueActionType.WriteDownTagsChanged,
        payload: value
    }), []);

    const setCategories = useCallback((value: ITagCategory[]) => dispatch({
        type: ObjectTagValueActionType.CategoriesChanged,
        payload: value
    }), []);

    const setDefaultCategory = useCallback((value: ITagCategory) => dispatch({
        type: ObjectTagValueActionType.DefaultCategoryChanged,
        payload: value
    }), []);

    const resetOptions = useCallback(() => dispatch({
        type: ObjectTagValueActionType.ResetOptionsRequested,
    }), []);

    const setView = useCallback((value: ObjectTagValuesViewEnum) => dispatch({
        type: ObjectTagValueActionType.ViewChanged,
        payload: value
    }), [])

    const setDuration = useCallback((value: CustomDurationDialogValues) => dispatch({
        type: ObjectTagValueActionType.DurationChanged,
        payload: value
    }), [])

    const setShowAlarmSetpointThreshold = useCallback((value: boolean) => dispatch({
        type: ObjectTagValueActionType.AlarmSetpointThresholdChanged,
        payload: value
    }), [])

    const setShowTagDescriptions = useCallback((value: boolean) => dispatch({
        type: ObjectTagValueActionType.TagDescriptionsChanged,
        payload: value
    }), [])

    const setGraphOptions = useCallback((value: ITrendingDisplayOptions) => dispatch({
        type: ObjectTagValueActionType.GraphDisplayOptionsChanged,
        payload: value
    }), []);

    const resetDurationOptions = useCallback(() => dispatch({
        type: ObjectTagValueActionType.ResetDefaultDuration,
        payload: undefined
    }), []);

    const setSelectedDate = useCallback((value: Date) => dispatch({
        type: ObjectTagValueActionType.SelectedDateChanged,
        payload: value
    }), []);

    const setEndDate = useCallback((value: Date) => dispatch({
        type: ObjectTagValueActionType.EndDateChanged,
        payload: value
    }), []);

    const setRefreshing = useCallback((value: boolean) => dispatch({
        type: ObjectTagValueActionType.IsRefreshingChanged,
        payload: value
    }), []);

    const setDownloadUid = useCallback((value: string | undefined) => dispatch({
        type: ObjectTagValueActionType.DownloadUidChanged,
        payload: value
    }), []);

    const setDataError = useCallback((value: boolean) => dispatch({
        type: ObjectTagValueActionType.DataErrorChanged,
        payload: value
    }), []);


    const setConfigurationID = useCallback((configurationIDOrCallback: number | undefined | ((configurationID: number | undefined) => number | undefined)) => dispatch({
        type: ObjectTagValueActionType.ConfigurationIDChanged,
        configurationIDOrCallback: configurationIDOrCallback
    }), []);

    const setDefaultConfigurationID = useCallback((defaultConfigurationID: number | undefined) => dispatch({
        type: ObjectTagValueActionType.DefaultConfigurationIDChanged,
        defaultConfigurationID: defaultConfigurationID
    }), []);

    const setColumnOptions = useCallback(
        (optionsOrCallback: ObjectTagValuesColumnOptionsState | ((options: ObjectTagValuesColumnOptionsState) => ObjectTagValuesColumnOptionsState)) => dispatch({
            type: ObjectTagValueActionType.ColumnOptionsChanged,
            optionsOrCallback: optionsOrCallback
    }), []);

    const setForceMinMax = useCallback((value: boolean) => dispatch({
        type: ObjectTagValueActionType.ForceMinMaxChanged,
        payload: value
    }), []);

    const setMinMaxError = useCallback((value: boolean) => dispatch({
        type: ObjectTagValueActionType.MinMaxErrorChanged,
        payload: value
    }), []);


    const setMinMaxY = useCallback((value: [number | undefined, number | undefined]) => dispatch({
        type: ObjectTagValueActionType.MinMaxYChanged,
        payload: value
    }), []);


    return {
        state,
        setInWriteDownMode,
        setGridApi,
        setData,
        setCommandInProgress,
        setLastDemandScanTimestamp,
        setPreWriteDownBeforeDemandScan,
        resetLiveDataState,
        setWriteDownTags,
        setCategories,
        setDefaultCategory,
        resetOptions,
        setView,
        setDuration,
        setShowAlarmSetpointThreshold,
        setShowTagDescriptions,
        setGraphOptions,
        setSelectedDate,
        setEndDate,
        setRefreshing,
        resetDurationOptions,
        setDownloadUid,
        setDataError,
        setConfigurationID,
        setDefaultConfigurationID,
        setColumnOptions,
        setForceMinMax,
        setMinMaxY,
        setMinMaxError,

    };
}

